import { z } from "zod";
import type { ManualPostType } from "./manual-post-types";

export type SocialPostFieldKey =
	| "youtubeTitle"
	| "youtubeVisibility"
	| "youtubeMadeForKids"
	| "youtubeContainsSyntheticMedia"
	| "youtubeCategoryId"
	| "youtubePlaylistId"
	| "youtubeFirstComment"
	| "instagramContentType"
	| "instagramShareToFeed"
	| "instagramCollaborators"
	| "instagramUserTags"
	| "instagramTrialGraduationStrategy"
	| "instagramThumbOffset"
	| "instagramThumbnail"
	| "instagramAudioName"
	| "instagramFirstComment"
	| "facebookDraft"
	| "facebookContentType"
	| "facebookTitle"
	| "facebookFirstComment"
	| "facebookPageId"
	| "facebookGeoCountries"
	| "linkedinDocumentTitle"
	| "linkedinOrganizationUrn"
	| "linkedinFirstComment"
	| "linkedinDisableLinkPreview"
	| "linkedinGeoCountries"
	| "xReplyToTweetId"
	| "xReplySettings"
	| "xThreadItems"
	| "xPollOptions"
	| "xPollDurationMinutes"
	| "xLongVideo"
	| "xGeoCountries"
	| "tiktokPrivacyLevel"
	| "tiktokAllowComment"
	| "tiktokAllowDuet"
	| "tiktokAllowStitch"
	| "tiktokContentPreviewConfirmed"
	| "tiktokExpressConsentGiven"
	| "tiktokVideoCoverTimestampMs"
	| "tiktokVideoCoverImageUrl"
	| "tiktokMediaType"
	| "tiktokPhotoCoverIndex"
	| "tiktokDescription"
	| "tiktokAutoAddMusic"
	| "tiktokVideoMadeWithAi"
	| "tiktokDraft"
	| "tiktokCommercialContentType";

export interface SocialPostPlatformFieldDefinition {
	key: SocialPostFieldKey;
	label: string;
	placeholder: string;
	description?: string;
	required: boolean;
}

export interface SocialPostPlatformRule {
	required: SocialPostFieldKey[];
	optional: SocialPostFieldKey[];
}

export interface SocialPostPlatformFields {
	youtubeTitle?: string;
	youtubeVisibility?: string;
	youtubeMadeForKids?: string;
	youtubeContainsSyntheticMedia?: string;
	youtubeCategoryId?: string;
	youtubePlaylistId?: string;
	youtubeFirstComment?: string;
	instagramContentType?: string;
	instagramShareToFeed?: string;
	instagramCollaborators?: string;
	instagramUserTags?: string;
	instagramTrialGraduationStrategy?: string;
	instagramThumbOffset?: string;
	instagramThumbnail?: string;
	instagramAudioName?: string;
	instagramFirstComment?: string;
	facebookDraft?: string;
	facebookContentType?: string;
	facebookTitle?: string;
	facebookFirstComment?: string;
	facebookPageId?: string;
	facebookGeoCountries?: string;
	linkedinDocumentTitle?: string;
	linkedinOrganizationUrn?: string;
	linkedinFirstComment?: string;
	linkedinDisableLinkPreview?: string;
	linkedinGeoCountries?: string;
	xReplyToTweetId?: string;
	xReplySettings?: string;
	xThreadItems?: string;
	xPollOptions?: string;
	xPollDurationMinutes?: string;
	xLongVideo?: string;
	xGeoCountries?: string;
	tiktokPrivacyLevel?: string;
	tiktokAllowComment?: string;
	tiktokAllowDuet?: string;
	tiktokAllowStitch?: string;
	tiktokContentPreviewConfirmed?: string;
	tiktokExpressConsentGiven?: string;
	tiktokVideoCoverTimestampMs?: string;
	tiktokVideoCoverImageUrl?: string;
	tiktokMediaType?: string;
	tiktokPhotoCoverIndex?: string;
	tiktokDescription?: string;
	tiktokAutoAddMusic?: string;
	tiktokVideoMadeWithAi?: string;
	tiktokDraft?: string;
	tiktokCommercialContentType?: string;
}

const EMPTY_RULE: SocialPostPlatformRule = { required: [], optional: [] };

const YOUTUBE_VIDEO_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"youtubeTitle",
	"youtubeVisibility",
	"youtubeMadeForKids",
	"youtubeContainsSyntheticMedia",
	"youtubeCategoryId",
	"youtubePlaylistId",
	"youtubeFirstComment",
];

const TIKTOK_REQUIRED_KEYS: SocialPostFieldKey[] = [
	"tiktokPrivacyLevel",
	"tiktokAllowComment",
	"tiktokContentPreviewConfirmed",
	"tiktokExpressConsentGiven",
];

const TIKTOK_VIDEO_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"tiktokAllowDuet",
	"tiktokAllowStitch",
	"tiktokVideoCoverTimestampMs",
	"tiktokVideoCoverImageUrl",
	"tiktokVideoMadeWithAi",
	"tiktokDraft",
	"tiktokCommercialContentType",
];

const TIKTOK_PHOTO_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"tiktokMediaType",
	"tiktokPhotoCoverIndex",
	"tiktokDescription",
	"tiktokAutoAddMusic",
	"tiktokDraft",
	"tiktokCommercialContentType",
];

const INSTAGRAM_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"instagramContentType",
	"instagramShareToFeed",
	"instagramCollaborators",
	"instagramUserTags",
	"instagramTrialGraduationStrategy",
	"instagramThumbOffset",
	"instagramThumbnail",
	"instagramAudioName",
	"instagramFirstComment",
];

const FACEBOOK_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"facebookDraft",
	"facebookContentType",
	"facebookTitle",
	"facebookFirstComment",
	"facebookPageId",
	"facebookGeoCountries",
];

const LINKEDIN_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"linkedinDocumentTitle",
	"linkedinOrganizationUrn",
	"linkedinFirstComment",
	"linkedinDisableLinkPreview",
	"linkedinGeoCountries",
];

const X_OPTIONAL_KEYS: SocialPostFieldKey[] = [
	"xReplyToTweetId",
	"xReplySettings",
	"xThreadItems",
	"xPollOptions",
	"xPollDurationMinutes",
	"xLongVideo",
	"xGeoCountries",
];

const RULES_BY_POST_TYPE: Record<
	ManualPostType,
	Record<string, SocialPostPlatformRule>
> = {
	video: {
		youtube: { required: [], optional: YOUTUBE_VIDEO_OPTIONAL_KEYS },
		tiktok: { required: TIKTOK_REQUIRED_KEYS, optional: TIKTOK_VIDEO_OPTIONAL_KEYS },
		instagram: { required: [], optional: INSTAGRAM_OPTIONAL_KEYS },
		facebook: { required: [], optional: FACEBOOK_OPTIONAL_KEYS },
		linkedin: { required: [], optional: LINKEDIN_OPTIONAL_KEYS },
		x: { required: [], optional: X_OPTIONAL_KEYS },
	},
	photo: {
		tiktok: { required: TIKTOK_REQUIRED_KEYS, optional: TIKTOK_PHOTO_OPTIONAL_KEYS },
		instagram: { required: [], optional: INSTAGRAM_OPTIONAL_KEYS },
		facebook: { required: [], optional: FACEBOOK_OPTIONAL_KEYS },
		linkedin: { required: [], optional: LINKEDIN_OPTIONAL_KEYS },
		x: { required: [], optional: X_OPTIONAL_KEYS },
	},
	text: {
		x: { required: [], optional: X_OPTIONAL_KEYS },
		linkedin: { required: [], optional: LINKEDIN_OPTIONAL_KEYS },
		facebook: { required: [], optional: FACEBOOK_OPTIONAL_KEYS },
	},
	document: {
		linkedin: { required: [], optional: LINKEDIN_OPTIONAL_KEYS },
	},
};

const FIELD_META: Record<
	SocialPostFieldKey,
	Omit<SocialPostPlatformFieldDefinition, "key" | "required">
> = {
	youtubeTitle: { label: "YouTube title", placeholder: "Video title (max 100 chars)" },
	youtubeVisibility: { label: "YouTube visibility", placeholder: "public, private, unlisted" },
	youtubeMadeForKids: { label: "YouTube madeForKids", placeholder: "true or false" },
	youtubeContainsSyntheticMedia: { label: "YouTube containsSyntheticMedia", placeholder: "true or false" },
	youtubeCategoryId: { label: "YouTube categoryId", placeholder: "e.g. 22" },
	youtubePlaylistId: { label: "YouTube playlistId", placeholder: "PLxxxxxxxx" },
	youtubeFirstComment: { label: "YouTube firstComment", placeholder: "First pinned comment" },
	instagramContentType: { label: "Instagram contentType", placeholder: "story or reels (leave empty for feed)" },
	instagramShareToFeed: { label: "Instagram shareToFeed", placeholder: "true or false" },
	instagramCollaborators: { label: "Instagram collaborators", placeholder: "username1,username2" },
	instagramUserTags: { label: "Instagram userTags JSON", placeholder: "[{\"username\":\"user\",\"x\":0.5,\"y\":0.5}]" },
	instagramTrialGraduationStrategy: { label: "Instagram trial graduationStrategy", placeholder: "MANUAL or SS_PERFORMANCE" },
	instagramThumbOffset: { label: "Instagram thumbOffset (ms)", placeholder: "0" },
	instagramThumbnail: { label: "Instagram thumbnail URL", placeholder: "https://..." },
	instagramAudioName: { label: "Instagram audioName", placeholder: "Custom audio name" },
	instagramFirstComment: { label: "Instagram firstComment", placeholder: "First comment text" },
	facebookDraft: { label: "Facebook draft", placeholder: "true or false" },
	facebookContentType: { label: "Facebook contentType", placeholder: "story or reel" },
	facebookTitle: { label: "Facebook title", placeholder: "Facebook specific title" },
	facebookFirstComment: { label: "Facebook firstComment", placeholder: "First comment text" },
	facebookPageId: { label: "Facebook pageId", placeholder: "Page ID" },
	facebookGeoCountries: { label: "Facebook geo countries", placeholder: "US,FR,DE" },
	linkedinDocumentTitle: { label: "LinkedIn documentTitle", placeholder: "Document title" },
	linkedinOrganizationUrn: { label: "LinkedIn organizationUrn", placeholder: "urn:li:organization:123456" },
	linkedinFirstComment: { label: "LinkedIn firstComment", placeholder: "First comment text" },
	linkedinDisableLinkPreview: { label: "LinkedIn disableLinkPreview", placeholder: "true or false" },
	linkedinGeoCountries: { label: "LinkedIn geo countries", placeholder: "US,FR,DE" },
	xReplyToTweetId: { label: "X replyToTweetId", placeholder: "tweet id" },
	xReplySettings: { label: "X replySettings", placeholder: "following, mentionedUsers, subscribers, verified" },
	xThreadItems: { label: "X threadItems JSON", placeholder: "[{\"content\":\"tweet 1\"},{\"content\":\"tweet 2\"}]" },
	xPollOptions: { label: "X poll options", placeholder: "Option 1,Option 2" },
	xPollDurationMinutes: { label: "X poll duration (minutes)", placeholder: "60" },
	xLongVideo: { label: "X longVideo", placeholder: "true or false" },
	xGeoCountries: { label: "X geo countries", placeholder: "US,FR,DE" },
	tiktokPrivacyLevel: { label: "TikTok privacy_level", placeholder: "PUBLIC_TO_EVERYONE, FOLLOWER_OF_CREATOR..." },
	tiktokAllowComment: { label: "TikTok allow_comment", placeholder: "true or false" },
	tiktokAllowDuet: { label: "TikTok allow_duet", placeholder: "true or false" },
	tiktokAllowStitch: { label: "TikTok allow_stitch", placeholder: "true or false" },
	tiktokContentPreviewConfirmed: { label: "TikTok content_preview_confirmed", placeholder: "true" },
	tiktokExpressConsentGiven: { label: "TikTok express_consent_given", placeholder: "true" },
	tiktokVideoCoverTimestampMs: { label: "TikTok video_cover_timestamp_ms", placeholder: "1000" },
	tiktokVideoCoverImageUrl: { label: "TikTok video_cover_image_url", placeholder: "https://..." },
	tiktokMediaType: { label: "TikTok media_type", placeholder: "photo" },
	tiktokPhotoCoverIndex: { label: "TikTok photo_cover_index", placeholder: "0" },
	tiktokDescription: { label: "TikTok description", placeholder: "Long caption for photo carousels" },
	tiktokAutoAddMusic: { label: "TikTok auto_add_music", placeholder: "true or false" },
	tiktokVideoMadeWithAi: { label: "TikTok video_made_with_ai", placeholder: "true or false" },
	tiktokDraft: { label: "TikTok draft", placeholder: "true or false" },
	tiktokCommercialContentType: { label: "TikTok commercialContentType", placeholder: "none, brand_organic, brand_content" },
};

export const socialPostPlatformFieldsSchema = z.object({
	youtubeTitle: z.string().trim().max(100).optional(),
	youtubeVisibility: z.string().trim().optional(),
	youtubeMadeForKids: z.string().trim().optional(),
	youtubeContainsSyntheticMedia: z.string().trim().optional(),
	youtubeCategoryId: z.string().trim().optional(),
	youtubePlaylistId: z.string().trim().optional(),
	youtubeFirstComment: z.string().trim().optional(),
	instagramContentType: z.string().trim().optional(),
	instagramShareToFeed: z.string().trim().optional(),
	instagramCollaborators: z.string().trim().optional(),
	instagramUserTags: z.string().trim().optional(),
	instagramTrialGraduationStrategy: z.string().trim().optional(),
	instagramThumbOffset: z.string().trim().optional(),
	instagramThumbnail: z.string().trim().optional(),
	instagramAudioName: z.string().trim().optional(),
	instagramFirstComment: z.string().trim().optional(),
	facebookDraft: z.string().trim().optional(),
	facebookContentType: z.string().trim().optional(),
	facebookTitle: z.string().trim().optional(),
	facebookFirstComment: z.string().trim().optional(),
	facebookPageId: z.string().trim().optional(),
	facebookGeoCountries: z.string().trim().optional(),
	linkedinDocumentTitle: z.string().trim().optional(),
	linkedinOrganizationUrn: z.string().trim().optional(),
	linkedinFirstComment: z.string().trim().optional(),
	linkedinDisableLinkPreview: z.string().trim().optional(),
	linkedinGeoCountries: z.string().trim().optional(),
	xReplyToTweetId: z.string().trim().optional(),
	xReplySettings: z.string().trim().optional(),
	xThreadItems: z.string().trim().optional(),
	xPollOptions: z.string().trim().optional(),
	xPollDurationMinutes: z.string().trim().optional(),
	xLongVideo: z.string().trim().optional(),
	xGeoCountries: z.string().trim().optional(),
	tiktokPrivacyLevel: z.string().trim().optional(),
	tiktokAllowComment: z.string().trim().optional(),
	tiktokAllowDuet: z.string().trim().optional(),
	tiktokAllowStitch: z.string().trim().optional(),
	tiktokContentPreviewConfirmed: z.string().trim().optional(),
	tiktokExpressConsentGiven: z.string().trim().optional(),
	tiktokVideoCoverTimestampMs: z.string().trim().optional(),
	tiktokVideoCoverImageUrl: z.string().trim().optional(),
	tiktokMediaType: z.string().trim().optional(),
	tiktokPhotoCoverIndex: z.string().trim().optional(),
	tiktokDescription: z.string().trim().optional(),
	tiktokAutoAddMusic: z.string().trim().optional(),
	tiktokVideoMadeWithAi: z.string().trim().optional(),
	tiktokDraft: z.string().trim().optional(),
	tiktokCommercialContentType: z.string().trim().optional(),
});

export function getSocialPostPlatformRule(
	postType: ManualPostType,
	platform: string,
): SocialPostPlatformRule {
	return RULES_BY_POST_TYPE[postType][platform] ?? EMPTY_RULE;
}

export function getSocialPostFieldDefinitionsForSelection(
	postType: ManualPostType,
	platforms: string[],
): SocialPostPlatformFieldDefinition[] {
	const required = new Set<SocialPostFieldKey>();
	const optional = new Set<SocialPostFieldKey>();
	for (const platform of platforms) {
		const rule = getSocialPostPlatformRule(postType, platform);
		for (const field of rule.required) required.add(field);
		for (const field of rule.optional) optional.add(field);
	}
	const keys = [...required, ...[...optional].filter((k) => !required.has(k))];
	return keys.map((key) => ({
		key,
		label: FIELD_META[key].label,
		placeholder: FIELD_META[key].placeholder,
		description: FIELD_META[key].description,
		required: required.has(key),
	}));
}

function invalidTriStateBoolean(value: string | undefined): boolean {
	if (!value || value.trim().length === 0) return false;
	const t = value.trim().toLowerCase();
	return !["true", "false", "1", "0", "yes", "no"].includes(t);
}

export function validateSocialPostPlatformFields(params: {
	postType: ManualPostType;
	platforms: string[];
	fields: SocialPostPlatformFields;
}): { missing: SocialPostFieldKey[]; invalid: string[] } {
	const missing = new Set<SocialPostFieldKey>();
	const invalid = new Set<string>();
	const required = getSocialPostFieldDefinitionsForSelection(
		params.postType,
		params.platforms,
	).filter((field) => field.required);
	for (const field of required) {
		const value = params.fields[field.key];
		if (!value || value.trim().length === 0) missing.add(field.key);
	}

	if (params.platforms.includes("youtube")) {
		const privacy = params.fields.youtubeVisibility?.trim().toLowerCase();
		if (privacy && !["public", "private", "unlisted"].includes(privacy))
			invalid.add("youtubeVisibility must be public, private, or unlisted");
	}
	for (const key of [
		"youtubeMadeForKids",
		"youtubeContainsSyntheticMedia",
		"instagramShareToFeed",
		"facebookDraft",
		"linkedinDisableLinkPreview",
		"xLongVideo",
		"tiktokAllowComment",
		"tiktokAllowDuet",
		"tiktokAllowStitch",
		"tiktokContentPreviewConfirmed",
		"tiktokExpressConsentGiven",
		"tiktokAutoAddMusic",
		"tiktokVideoMadeWithAi",
		"tiktokDraft",
	] as SocialPostFieldKey[]) {
		if (invalidTriStateBoolean(params.fields[key]))
			invalid.add(`${key} must be true or false`);
	}

	return { missing: [...missing], invalid: [...invalid] };
}

export function pickSocialPostPlatformFieldsForSelection(params: {
	postType: ManualPostType;
	platforms: string[];
	fields: SocialPostPlatformFields;
}): SocialPostPlatformFields {
	const allowed = new Set(
		getSocialPostFieldDefinitionsForSelection(
			params.postType,
			params.platforms,
		).map((field) => field.key),
	);
	const next: SocialPostPlatformFields = {};
	for (const key of Object.keys(params.fields) as SocialPostFieldKey[]) {
		if (!allowed.has(key)) continue;
		const value = params.fields[key];
		if (!value || value.trim().length === 0) continue;
		next[key] = value.trim();
	}
	return next;
}
